import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/* ---------------------------------------------------------------------------
 * Click-to-open header menu.
 * -------------------------------------------------------------------------
 * The submenu opens on CLICK (tap) instead of hover, and closes on a second
 * click, an outside click, or the Escape key.
 *
 * The caret/arrow icon is rendered in the Liquid template (server-side) so it
 * is permanent and cannot be wiped by section hydration. This file only
 * handles behaviour, no DOM/icon injection.
 * ------------------------------------------------------------------------- */

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);

    // Click-to-open behaviour.
    this.addEventListener('click', this.toggle);
    document.addEventListener('click', this.#documentClickListener);
    document.addEventListener('keydown', this.#keydownListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('click', this.toggle);
    document.removeEventListener('click', this.#documentClickListener);
    document.removeEventListener('keydown', this.#keydownListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Hover-open is intentionally disabled. The theme's markup still binds
   * on:pointerenter/on:pointerleave/on:focus/on:blur to these two methods, so
   * we keep them as no-ops instead of removing the bindings from the Liquid.
   */
  activate = () => {};
  deactivate() {}

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Toggle a submenu open/closed on click.
   * Items without a submenu (Home, Contact, Blog) navigate normally.
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = /** @type {HTMLElement | null} */ (event.target.closest('[ref="menuitem"]'));
    if (!item) return;

    const submenu = findSubmenu(item);
    if (!submenu) return; // no submenu -> let the link navigate

    // Category link with a submenu: open/close it instead of navigating.
    event.preventDefault();

    if (this.#state.activeItem === item) {
      this.#closeSubmenu();
    } else {
      this.#openSubmenu(item);
    }
  };

  /**
   * Open the submenu for the given item and measure its height.
   * @param {HTMLElement} item
   */
  #openSubmenu(item) {
    if (!this.headerComponent) return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    const isDefaultSlot = !item.closest('[slot="overflow"]');
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    // Close any other open item first.
    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem && previouslyActiveItem !== item) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu && submenu.offsetHeight > 0) {
              this.#applyHeight(item, submenu, isDefaultSlot, hasSubmenu);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);

      // Re-measure after layout settles (handles content-visibility / late layout).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#applyHeight(item, submenu, isDefaultSlot, hasSubmenu));
      });

      // Re-measure when lazy images inside the submenu finish loading, so image
      // menus (collection images, featured products) don't get clipped to 0 height.
      submenu.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        const remeasure = () => this.#applyHeight(item, submenu, isDefaultSlot, hasSubmenu);
        img.addEventListener('load', remeasure, { once: true });
        img.addEventListener('error', remeasure, { once: true });
      });
    }

    // Initial measurement. May read a too-small height before images/layout are
    // ready; the re-measure callbacks above will correct it.
    this.#applyHeight(item, submenu, isDefaultSlot, hasSubmenu);
  }

  /**
   * Close the currently open submenu.
   * @param {HTMLElement | null} [item]
   */
  #closeSubmenu = (item = this.#state.activeItem) => {
    if (!item) return;

    const submenu = findSubmenu(item);

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Close when clicking anywhere outside the header menu.
   * @param {MouseEvent} event
   */
  #documentClickListener = (event) => {
    if (!this.#state.activeItem) return;
    const target = event.target;
    if (target instanceof Node) {
      if (this.contains(target)) return;
      if (this.overflowMenu?.contains(target)) return;
    }
    this.#closeSubmenu();
  };

  /**
   * Close on Escape and return focus to the trigger.
   * @param {KeyboardEvent} event
   */
  #keydownListener = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;
    const toFocus = this.#state.activeItem;
    this.#closeSubmenu();
    if (toFocus instanceof HTMLElement) toFocus.focus();
  };

  /**
   * Measure the submenu height using the overflow-aware rules.
   * @param {HTMLElement | null} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @returns {number}
   */
  #computeHeight(submenu, isDefaultSlot, hasSubmenu) {
    if (!submenu) return 0;

    let finalHeight = submenu.offsetHeight || 0;

    // For overflow menu, the height needs to be either the submenu content or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding.
         * Measuring the overflowMenu.offsetHeight is easier than adding the padding variables. */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    return finalHeight;
  }

  /**
   * Apply the measured height to the CSS variables, but only while this item is
   * still the active one. Guarding on the active item prevents late async
   * callbacks (image load / rAF) from re-opening a menu that already closed.
   * @param {HTMLElement | null} item
   * @param {HTMLElement | null} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   */
  #applyHeight(item, submenu, isDefaultSlot, hasSubmenu) {
    if (!this.headerComponent) return;
    if (!item || this.#state.activeItem !== item) return;

    const finalHeight = this.#computeHeight(submenu, isDefaultSlot, hasSubmenu);

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}